import * as React from "react";
import { isPlainObject } from "lodash";
const { hasOwnProperty } = Object.prototype;

interface Option {
  label: string;
  value: string;
  disabled?: boolean;
}

export interface Select {
  options: Option[];
}

export const isOption = (x: any): x is Option =>
  isPlainObject(x) &&
  hasOwnProperty.call(x, "label") &&
  hasOwnProperty.call(x, "value") &&
  typeof x.label === "string" &&
  typeof x.value === "string";

export const isSelect = (x: any): x is Select =>
  x &&
  isPlainObject(x) &&
  hasOwnProperty.call(x, "options") &&
  Array.isArray(x.options) &&
  x.options.every(isOption);

export type AcceptedInputType = "text" | "textarea" | "date" | Select;

// These will be passed through directly to the underlying inputs.
interface SupportedHTMLAttributes {
  readOnly?: boolean;
  tabIndex?: number;
  autoFocus?: boolean;
  placeholder?: string;
}

interface InputProps extends SupportedHTMLAttributes {
  acceptedInput: AcceptedInputType;
  rows?: number;
  onChange: any;
  value: any;
}

export const Input: React.SFC<InputProps> = (props: InputProps) => {
  const { acceptedInput, rows, value, onChange, placeholder, ...otherProps } = props;
  if (acceptedInput === "date" || acceptedInput === "text") {
    return <input type={acceptedInput} value={value} onChange={onChange} {...otherProps} />;
  }
  if (acceptedInput === "textarea") {
    return (
      <textarea
        rows={rows || 3}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        {...otherProps}
      />
    );
  }
  const { options } = acceptedInput;
  return (
    <select value={value} onChange={onChange} {...otherProps}>
      {options.map(opt => {
        if (opt.disabled) {
          return <React.Fragment key={opt.value} />;
        }
        return (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        );
      })}
    </select>
  );
};
